/**
 * Spec 79 M1.5 D5 — ToolCallCard
 *
 * Renders a conversation_messages row with role='tool' as a collapsible
 * chip. Parses the JSON content against IntentDrafterToolCallContentSchema.
 * If the parse fails (malformed JSON or schema mismatch), renders nothing —
 * these cards are auxiliary, non-essential UI.
 *
 * Clicking "View →" opens the right panel via useContextPanel().openPanel().
 * Clicking the header row expands the card to show the top-3 search results.
 *
 * Design intentionally subtler than CloseSummaryCard (smaller font, muted
 * chip background) — tool calls are observability data, not primary content.
 */
import React, { useState } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import { IntentDrafterToolCallContentSchema } from '@confluent/shared/lib/intent/types';
import { parsePanelUrl, useContextPanel } from '../context-panel/url-state.js';
import { Button } from '../ui/Button.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MessageLike {
  id: string;
  role: string;
  content: string;
  createdAt: string;
}

interface ToolCallCardProps {
  message: MessageLike;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Human-readable summary label for a drafter outcome.
 */
function outcomeLabel(drafterChose: 'advised' | 'drafted' | 'stub', resultCount: number): string {
  if (drafterChose === 'advised') return `found ${resultCount} match${resultCount === 1 ? '' : 'es'}`;
  if (drafterChose === 'drafted') return 'created draft';
  return 'no match';
}

/**
 * True when this tool row is one the card can actually draw.
 *
 * The unified agent runtime writes a different envelope (`kind: 'unified_tool_call'`), and
 * this card silently renders nothing for it — which used to leave an empty bordered bubble
 * carrying only a timestamp in the stream. Callers use this to skip the row entirely.
 */
export function isRenderableToolCall(content: string): boolean {
  try {
    return IntentDrafterToolCallContentSchema.safeParse(JSON.parse(content)).success;
  } catch {
    return false;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ToolCallCard: React.FC<ToolCallCardProps> = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const { openPanel } = useContextPanel();

  // Parse — silent degrade on failure.
  let parsed: ReturnType<typeof IntentDrafterToolCallContentSchema.safeParse> | null = null;
  try {
    const raw: unknown = JSON.parse(message.content);
    parsed = IntentDrafterToolCallContentSchema.safeParse(raw);
  } catch {
    return null;
  }

  if (!parsed?.success) return null;

  const { toolCall } = parsed.data;
  const { name, drafterChose, panelLink, output } = toolCall;

  const resultCount = output.results.length;
  const label = outcomeLabel(drafterChose, resultCount);

  const panelPointer = parsePanelUrl(
    // panelLink may be a full ?panel= query param value or just the value portion.
    panelLink.startsWith('?panel=') ? panelLink.slice('?panel='.length) : panelLink,
  );

  const top3 = output.results.slice(0, 3);

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (panelPointer) {
      openPanel(panelPointer);
    }
  };

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className="bg-surface border rounded-md text-xs">
      {/* Chip header row — clickable to expand */}
      <div
        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleToggle();
        }}
        aria-expanded={expanded}
      >
        {/* Expand chevron */}
        <span className="text-faint shrink-0">{expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>

        {/* Tool icon */}
        <Search size={12} className="text-muted shrink-0" aria-hidden="true" />

        {/* Tool name + outcome */}
        <span className="text-muted font-mono">{name}</span>
        <span className="text-faint">·</span>
        <span className={drafterChose === 'stub' ? 'text-warning' : 'text-success'}>{label}</span>

        {/* Spacer */}
        <span className="flex-1" />

        {/* View → action */}
        {panelPointer && (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto shrink-0 p-0 text-primary-500 hover:underline"
            onClick={handleViewClick}
            aria-label={`View ${name} results in panel`}
          >
            View →
          </Button>
        )}
      </div>

      {/* Expandable results */}
      {expanded && top3.length > 0 && (
        <div className="border-t px-3 py-2 space-y-1">
          {top3.map((item, i) => (
            <div key={item.id ?? `result-${i}`} className="flex items-center gap-2">
              <span className="text-faint w-4 text-right shrink-0">{i + 1}.</span>
              <span className="text-body truncate flex-1">{item.name}</span>
              <span className="text-faint shrink-0 tabular-nums">{Math.round(item.score * 100)}%</span>
            </div>
          ))}
          {output.results.length === 0 && <p className="text-faint italic">No results found.</p>}
        </div>
      )}

      {/* Expanded with no results */}
      {expanded && top3.length === 0 && (
        <div className="border-t px-3 py-2">
          <p className="text-faint italic">No results found.</p>
        </div>
      )}
    </div>
  );
};
